import React, { useCallback, useEffect, useState } from 'react';
import {
  Image,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import RefundTimeline from '../components/booking/RefundTimeline';
import SosButton from '../components/sos/SosButton';
import Button from '../components/ui/Button';
import ConfirmSheet from '../components/ui/ConfirmSheet';
import Icon, { IconName } from '../components/ui/Icon';
import Text from '../components/ui/Text';
import {
  Booking,
  bookingStages,
  Professional,
  stageIndex,
} from '../data/bookings';
import { cancellationPolicy } from '../data/booking';
import { canCancel, canReschedule } from '../data/cancellation';
import useJobProgress from '../hooks/useJobProgress';
import ExtendTimeCard from '../components/booking/ExtendTimeCard';

/**
 * A start time as a clock reads it.
 *
 * The booking carries the instant, because the running timer counts from it -
 * and an instant put on screen unformatted is "2026-09-23T07:39:06.446Z",
 * which is what this had been showing a customer mid-job.
 */
const clockTime = (value: string | undefined): string => {
  if (!value) return '';
  const at = new Date(value);
  return Number.isNaN(at.getTime())
    ? value
    : at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};
import { hasInvoice } from '../data/invoice';
import { refundFor } from '../data/refunds';
import { MyReview, visiblePhotos } from '../data/reviews';
import { openWhatsappSupport } from '../services/support';
import { useVoiceEligibility } from '../voice/VoiceCallProvider';
import { eligibilityCopy } from '../voice/types';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../theme';

/** Whole rupees, grouped - `Intl` is not reliably present on Android JSC. */
const rupees = (value: number) =>
  '₹' + String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** Stars in the rating row. */
const STARS = [1, 2, 3, 4, 5];

/** What each star means, so a one-tap rating is not left to interpretation. */
const STAR_WORDS = ['', 'Poor', 'Not great', 'Fine', 'Good', 'Excellent'];

type Props = {
  booking: Booking;
  onBack: () => void;
  /** Opens the thread with this booking's professional. */
  onMessage?: (booking: Booking) => void;
  /**
   * Places an in-app voice call to this booking's professional.
   *
   * Sits beside `onMessage` because the two are the same kind of thing - the
   * two ways to reach a person - and are threaded through `ProCard` together.
   * Neither exchanges a phone number: a call is carried over the internet
   * between the two apps, which is why the button says "Call in app".
   *
   * Whether it is *offered* is not this handler's business. The screen asks
   * the platform (`useVoiceEligibility` below) and draws the control disabled
   * with its reason attached when the answer is no.
   *
   * The second argument is the name the platform says the call would reach,
   * which is not always `pro.name` - the booking's professional can be
   * reassigned, and the voice service is the side that knows who would
   * actually be rung. It exists for one failure: a call the server could not
   * place at all has to name who it could not reach, and at that point there
   * is no call object left to read a name from.
   */
  onCall?: (booking: Booking, counterpartName?: string | null) => void;
  /**
   * Whether there is an account behind this screen.
   *
   * Only the Call button reads it, and only to stay LIVE when the answer is no
   * - see the note beside `callGated` below. Defaults to true so a screen
   * rendered on its own (the test suite does exactly that) behaves as it did.
   */
  signedIn?: boolean;
  /** The review already left for this booking, if there is one. */
  review?: MyReview;
  /** Opens the review composer - new or, given `review`, the editor. */
  onRate?: (booking: Booking) => void;
  /**
   * Records a rating from the stars on this screen, without opening anything.
   *
   * The stars are a control in their own right: one tap is the whole rating.
   * The composer stays behind "Rate & Review" for the people who also want to
   * write something, attach a photo or pick tags.
   */
  onQuickRate?: (booking: Booking, stars: number) => void;
  /** Opens the tax invoice. Only offered once the work is complete. */
  onOpenInvoice?: (booking: Booking) => void;
  /** Opens the cancel sheet, which decides the window and the refund. */
  onCancel?: (booking: Booking) => void;
  /**
   * Opens the slot picker to move this booking.
   *
   * Sits beside cancelling because both are "I cannot keep this booking",
   * and somebody who opens that disclosure wants whichever of the two suits
   * them - offering only the refund pushes people toward cancelling a booking
   * they would have kept.
   *
   * They are NOT the same terms, though the copy above once said so.
   * Rescheduling is free at any distance and needs two hours' notice;
   * cancelling is free until six hours out and charges a fee inside that.
   * `cancellationPolicy.detail` states both, from the constants.
   */
  onReschedule?: (booking: Booking) => void;
  /** Opens the safety screen, where a press-and-hold raises the alarm. */
  onSos?: (booking: Booking) => void;
  /** Disputes a completed job - a ticket with evidence, never a cancel. */
  onDispute?: (booking: Booking) => void;
  /** Books the same thing again, offered after an ops cancellation. */
  onRebook?: (booking: Booking) => void;
  /**
   * Open the gateway for a booking that was placed but never paid for.
   *
   * An online booking exists before any money is asked for, so backing out
   * of the sheet leaves one behind. Without a way back to the payment the
   * customer can only watch it expire and book the whole thing again.
   */
  onPay?: (booking: Booking) => void;
  /**
   * Re-fetches this booking from the platform - the header's refresh button.
   *
   * Refetches the whole list rather than this one row, because that is the
   * only read `useBookings` offers; the caller is expected to reconcile the
   * open booking against the refreshed list once it lands. Omitted where
   * there is nowhere to refresh from - a booking shown outside `HomeScreen`,
   * for instance in a test.
   */
  onRefresh?: () => void;
  /** True while that refetch is in flight, for the button's spinner. */
  refreshing?: boolean;
  /**
   * Issues a fresh end-of-service code, at the customer's request.
   *
   * The professional cannot do this for themselves - the route is
   * authenticated as the booking's owner, which is the whole point of a
   * handshake - so after five wrong entries this button is the only thing in
   * the product that unsticks the job. Omitted where there is nothing to call
   * (a booking rendered outside `HomeScreen`, or a test), and the control is
   * then not drawn rather than drawn and dead.
   */
  onResendEndOtp?: (booking: Booking) => Promise<void>;
};

/**
 * One booking, in full.
 *
 * The page is the same for every stage; only the panel under the services
 * changes. That is deliberate - the address, the payment, the invoice and the
 * help links are where they were an hour ago and where they will be tomorrow,
 * and a screen that rearranges itself as work progresses makes someone hunt
 * for the cancel link at the one moment they want it most.
 *
 * ---------------------------------------------------------------------------
 * WHICH PANEL SHOWS
 * ---------------------------------------------------------------------------
 *   completed          the rating form
 *   service            started-at and the running duration
 *   onway + otp        the start code - arriving is the end of "on the way",
 *                      not a sixth step, so the stepper does not move for it
 *   onway              the ETA, and only the ETA - see below
 *   assigned / booked  who is coming
 *
 * ---------------------------------------------------------------------------
 * THERE IS NO LIVE TRACKING, BY DECISION
 * ---------------------------------------------------------------------------
 * The "on the way" panel shows a time and nothing else: no map, no distance,
 * no moving dot. Nothing feeds this app a professional's position, and
 * plotting an invented one would look like tracking and be believed. It is
 * also somebody's live location, which is not ours to broadcast for the length
 * of a shift so that a customer can watch a van.
 *
 * The removed pieces are commented out in place rather than deleted. See the
 * long note on the `onway` branch before turning any of it back on.
 *
 * ---------------------------------------------------------------------------
 * NOTHING HERE IS LIVE.
 * ---------------------------------------------------------------------------
 * The stage comes from seed data in `src/data/bookings.ts` and never advances.
 * The ETA does not count down, the OTP is fixed, and Rate & Review records
 * nothing. Each is one API call away and none of them exists yet.
 */
function BookingDetailScreen({
  booking,
  onBack,
  onMessage,
  onCall,
  signedIn = true,
  review,
  onRate,
  onQuickRate,
  onOpenInvoice,
  onCancel,
  onReschedule,
  onSos,
  onDispute,
  onRebook,
  onPay,
  onRefresh,
  refreshing = false,
  onResendEndOtp,
}: Props) {
  const insets = useSafeAreaInsets();
  const resp = useResponsive();

  /*
   * The resend, and what it says afterwards.
   *
   * A refetch is what actually replaces the digits on screen, so the handler
   * ends by asking for one rather than trusting a response body - the route
   * returns nothing, deliberately.
   *
   * The notice stays until the panel goes away with the job. There is no
   * cooldown here and there should not be one invented: the server owns that
   * rule, and a countdown this screen made up would either refuse a customer
   * the server would have served, or promise one it will refuse.
   */
  const [resendingCode, setResendingCode] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);

  /*
   * The end code is behind a door now, and the door asks a question.
   *
   * Reading the code out IS signing off - it ends the job, releases the
   * payment and closes the visit. It used to sit on screen for the whole
   * visit with a line of copy asking the customer not to share it early,
   * which is a sentence next to a number: the professional says "code?",
   * the customer reads the number, and nobody has looked round the room.
   *
   * So the number is not drawn until they have been asked whether the work
   * is actually finished and their things are where they should be. It is
   * one tap and one question, and it is the only moment in the visit where
   * the customer has any reason to stop and check.
   *
   * Reset per visit rather than remembered: this is a prompt to look NOW,
   * and one the app remembers answering is a prompt nobody reads twice.
   */
  const [endCodeShown, setEndCodeShown] = useState(false);
  const [checkingBeforeCode, setCheckingBeforeCode] = useState(false);

  const resendCode = async () => {
    if (resendingCode || !onResendEndOtp) {
      return;
    }

    setResendingCode(true);
    setResendNotice(null);
    try {
      await onResendEndOtp(booking);
      setResendNotice('New code issued. Read out the one now shown above.');
    } catch (caught) {
      /*
       * Said plainly rather than raised as an alert. The customer is standing
       * next to the professional working out what to do, and a modal they have
       * to dismiss before they can read the code again is the wrong shape for
       * that moment.
       */
      setResendNotice(
        caught instanceof Error
          ? caught.message
          : 'Could not issue a new code. Try again in a moment.',
      );
    } finally {
      setResendingCode(false);
    }
  };

  /*
   * Whether the professional can be rung right now, asked before the button is
   * drawn rather than when it is pressed.
   *
   * An affordance that fails on tap is worse than one that was never offered,
   * and every reason it can be refused - nobody assigned yet, the job is over,
   * this customer is already on a call - is something visible on this very
   * page. So the answer is fetched here and the control states its own reason.
   *
   * ---------------------------------------------------------------------------
   * WHY IT IS KEYED ON MORE THAN THE ID
   * ---------------------------------------------------------------------------
   * `booking.id` never changes for an open booking, so keyed on that alone the
   * answer was fetched once and then frozen for as long as the screen stayed
   * open. The most common thing this page is watched for is precisely the thing
   * that unfreezes it: a professional being assigned. Open the booking before
   * assignment, pull to refresh until somebody appears, and "Call in app"
   * stayed dark reading "Calling opens once a professional is assigned" - for
   * ever, until the screen was closed and opened again.
   *
   * So the stage, the status and who is assigned all go into the key. All three
   * come from the same refreshed booking `HomeScreen` hands down, so eligibility
   * re-asks in the same beat the rest of the page updates in.
   */
  const callable = useVoiceEligibility(
    'booking',
    booking.id,
    `${booking.stage}:${booking.status}:${booking.professional?.name ?? ''}`,
  );

  /**
   * The refresh button, with the call's own question folded into it.
   *
   * `onRefresh` refetches the booking; eligibility is a separate request to a
   * separate route, and the key above only re-asks once the refreshed booking
   * has actually landed. Reloading both from the one press means a customer who
   * presses refresh *because* the button is dark gets an answer either way,
   * including when the refetched booking comes back identical.
   *
   * Not memoised. `useAsync` hands back a fresh `reload` on every render, so a
   * `useCallback` here would claim a stability it cannot have - and this goes
   * straight onto a `Pressable`, which is not memoised either.
   */
  const refreshAll = () => {
    onRefresh?.();
    callable.reload();
  };

  /*
   * Signed out, the Call button stays live and means "sign in".
   *
   * `useVoiceEligibility` holds off entirely without a session - it has nothing
   * to authenticate with - so `canCall` is false, which used to disable the
   * button, which meant its `onPress` never fired, which meant the `gated`
   * wrapper `HomeScreen` puts around it (the one whose whole job is to raise
   * the auth sheet) could never run. A Call button that silently does nothing
   * is worse than one that asks you to sign in, and the machinery to ask was
   * already there and unreachable.
   *
   * So a signed-out screen draws the button as offered, and the press goes to
   * `onCall` -> `gated` -> the auth sheet, with the call itself parked until
   * verification finishes. Nothing here decides whether the call is allowed:
   * that is still the platform's answer, asked the moment there is a session
   * to ask with.
   */
  const callGated = !signedIn;

  const [servicesOpen, setServicesOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  /*
   * The star just tapped, held here until the saved review comes back.
   *
   * `onQuickRate` is a round trip, and a star that stays hollow for the length
   * of it reads as a tap that missed. Cleared whenever the stored review
   * catches up, so the screen goes back to showing the real thing rather than
   * a local guess that could outlive a failed save.
   */
  const [justRated, setJustRated] = useState(0);

  useEffect(() => {
    setJustRated(0);
  }, [review?.stars, booking.id]);

  /*
   * The running clock, live only while work is actually happening.
   *
   * Gated on the stage rather than on `startedAt` alone: a completed booking
   * still carries the instant it began, and counting up from it would tell
   * somebody their finished job is nine hours in.
   */
  /*
   * Opens WhatsApp with the message already written.
   *
   * The reference and not the id: `reference` is the string the customer sees
   * on this screen and the one support searches on - `booking.id` is a UUID
   * nobody on either end of the chat can use. The service and the slot go in
   * too, so the first message answers the three questions the desk would
   * otherwise have to ask before it could even look the booking up.
   *
   * Left editable rather than sent: it lands in WhatsApp's input, so a
   * customer can add what is actually wrong before they send it, and can see
   * exactly what the app is about to say on their behalf.
   */
  const messageSupport = useCallback(() => {
    openWhatsappSupport(
      `Hi Homingo, I need help with my booking ${booking.reference} - ${booking.title}, ${booking.when}.`,
    );
  }, [booking.reference, booking.title, booking.when]);

  const progress = useJobProgress(
    booking.startedAt,
    booking.duration,
    booking.stage === 'service',
  );

  /*
   * Minutes this visit could still take, as the SERVER works it out.
   *
   * The cap is the service's own and ops can change it, so the app asking
   * its own copy of the arithmetic is how a customer ends up looking at an
   * offer the server refuses - or at no offer where it would have said yes.
   */
  const minutesLeft = booking.extendableMinutes ?? 0;

  /*
   * When this job really ends, which is not the slot it was sold for.
   *
   * A Pro who starts an hour early finishes an hour early, and the server
   * measures both the fifteen-minute cutoff and the extension itself from
   * the real end - so the card has to, or it offers an extension the server
   * refuses (and hides one it would have allowed).
   */
  const runsUntil = (() => {
    if (!booking.startedAt || !booking.slotStartAt || !booking.slotEndAt) {
      return booking.slotEndAt;
    }
    const span =
      new Date(booking.slotEndAt).getTime() -
      new Date(booking.slotStartAt).getTime();
    const end = new Date(new Date(booking.startedAt).getTime() + span);
    return Number.isNaN(end.getTime()) ? booking.slotEndAt : end.toISOString();
  })();

  const current = stageIndex(booking.stage);
  const done = booking.stage === 'completed';
  /*
   * Placed, unpaid, and therefore not live work.
   *
   * Read from the booking rather than derived from the stage: the server puts
   * it in awaiting_payment, and the stage machine deliberately folds that in
   * with created and assigning, which are states where something IS happening.
   */
  const awaitingPayment = booking.awaitingPayment === true;
  /* The optimistic tap wins while it is in flight; the stored review after. */
  const shownStars = justRated || review?.stars || 0;
  /*
   * Once there is a rating, the stars read it out rather than take a new one.
   *
   * The server refuses to change a stored rating - `ratingSum`, the dispatch
   * tie-break and the Pro's five-star bonus are all computed from it, and there
   * is no path that unwinds those. It used to answer the attempt with a 201
   * carrying the OLD number, so tapping three on a five-star booking looked
   * exactly like it had worked and changed nothing. That silence is gone, which
   * is why this has to be: the tap would now surface "Your review was not
   * saved" for a control the screen should not have offered.
   *
   * Only the number is fixed. "Edit your review" below still opens the
   * composer, and the words, tags and photographs it sends DO now save - that
   * second step was being discarded by the same bug and is the half of it that
   * was worth keeping.
   */
  const ratingIsFinal = shownStars > 0;
  const pro = booking.professional;
  const cancelled = booking.status === 'cancelled';
  const refund = refundFor(booking);
  /*
   * The alarm belongs on a visit that is happening, and only then. On a
   * booking made for next Tuesday it is noise; on a completed one it is a
   * button that would raise an alarm about nothing.
   */
  const live =
    !cancelled &&
    (booking.stage === 'assigned' ||
      booking.stage === 'onway' ||
      booking.stage === 'service');

  return (
    <View style={styles.root}>
      {/* ------------------------------------------------------------ header */}
      <View
        style={[
          styles.header,
          /* Pinned above the scroller, so it centres on the page's column
             itself. Zero on a phone. */
          { paddingHorizontal: screenPadding + resp.sideInset },
        ]}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back"
          onPress={onBack}
          hitSlop={12}
        >
          <Icon name="chevronLeft" size={22} color={colors.ink} />
        </Pressable>

        <Text variant="title" align="center" style={styles.headerTitle}>
          Booking Details
        </Text>

        <View style={styles.headerIcon}>
          <Icon name="shieldCheck" size={23} color={colors.ink} />
        </View>

        {/*
          The refresh button that sat here is gone - pulling the page down is
          the refresh now, the same gesture as the bookings list, and the
          screen also re-reads itself while the job is live (see `HomeScreen`)
          so most of the time nobody has to ask at all.
        */}
      </View>

      <ScrollView
        contentContainerStyle={[
          styles.page,
          { paddingBottom: Math.max(insets.bottom, spacing.md) + spacing.xl },
          /* `page` above already carries the gutter, and `resp.column` is the
             centring inset ALONE - zero on a phone - so appending it wiped
             that gutter and ran the content into the screen edge. `resp.page`
             is the inset plus the gutter. */
          resp.page,
        ]}
        showsVerticalScrollIndicator={false}
        /*
          The same gesture the bookings list answers, and it re-asks the call
          eligibility too - `refreshAll` - so someone pulling BECAUSE the Call
          button is dark gets an answer either way.
        */
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={refreshAll}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          ) : undefined
        }
      >
        {/* ------------------------------------------------------- the facts */}
        <View style={styles.section}>
          <View style={styles.statusRow}>
            {/*
              Three states, because an unpaid booking is not live work.

              It was drawn as LIVE alongside "Finding a professional", which
              is the app inventing activity: an online booking sits unpaid
              until the gateway settles, dispatch never sees it, and the
              server releases it after the hold window. A customer who backed
              out of the sheet was being told somebody was on their way to
              being found.
            */}
            <View
              style={[
                styles.badge,
                done || awaitingPayment ? styles.badgeDone : null,
              ]}
            >
              <Text
                variant="eyebrow"
                color={
                  done || awaitingPayment ? colors.inkMuted : colors.primary
                }
              >
                {awaitingPayment
                  ? 'PAYMENT PENDING'
                  : done
                  ? 'COMPLETED'
                  : 'LIVE'}
              </Text>
            </View>
            <Text variant="caption" color={colors.inkMuted}>
              Booking ID {booking.reference}
            </Text>
          </View>

          <Text variant="titleLg" style={styles.title}>
            {booking.title}
          </Text>
          <Text variant="caption" color={colors.inkMuted}>
            {booking.services.length}{' '}
            {booking.services.length === 1 ? 'service' : 'services'} ·{' '}
            {booking.tags.length} {booking.tags.length === 1 ? 'tag' : 'tags'}
          </Text>

          <View style={styles.factRow}>
            <Icon name="calendar" size={15} color={colors.inkMuted} />
            <Text variant="caption" color={colors.inkMuted}>
              {booking.when} · {booking.duration} (Estimated)
            </Text>
          </View>

          {/* Only a recurring booking has one - see the note on `Booking.cadence`.
              `when` above is the next visit; this is how often the ones after
              it come, which `when` alone does not say. */}
          {booking.cadence ? (
            <View style={styles.factRow}>
              <Icon name="refresh" size={15} color={colors.inkMuted} />
              <Text variant="caption" color={colors.inkMuted}>
                {booking.cadence}
              </Text>
            </View>
          ) : null}

          <View style={styles.factRow}>
            <Icon name="pin" size={15} color={colors.inkMuted} />
            <Text
              variant="caption"
              color={colors.inkMuted}
              style={styles.factFill}
            >
              {booking.address}
            </Text>
          </View>
        </View>

        {/* ----------------------------------------------------- the services */}
        <Disclosure
          icon="calendar"
          title="Services"
          detail={`${booking.services.length} items`}
          open={servicesOpen}
          onToggle={() => setServicesOpen(open => !open)}
          summary={booking.tags.slice(0, 2).join(', ')}
        >
          {booking.services.map(service => (
            <View key={service} style={styles.serviceLine}>
              <Icon
                name="check"
                size={14}
                color={colors.primary}
                strokeWidth={2.4}
              />
              <Text variant="bodySm">{service}</Text>
            </View>
          ))}
        </Disclosure>

        {/* -------------------------------------------------- the stage panel */}
        {done ? (
          <View style={styles.section}>
            <Text variant="subtitle" align="center" style={styles.doneTitle}>
              Service completed
            </Text>

            <View style={styles.doneMark}>
              <Icon
                name="check"
                size={30}
                color={colors.onPrimary}
                strokeWidth={2.6}
              />
            </View>

            <Text variant="caption" align="center" color={colors.inkMuted}>
              Thanks for choosing Homingo!
            </Text>

            <Text variant="subtitle" align="center" style={styles.rateTitle}>
              {shownStars ? 'Your rating' : 'How was your experience?'}
            </Text>

            {/*
              The stars submit. They do not open anything.

              Tapping one records that rating there and then - the whole of a
              five-star rating is one tap, and most people want nothing more
              than that. Opening a sheet on the first star made them confirm a
              decision they had already made.

              The composer is still one tap away below, for the people who
              also want to write something, attach a photo or pick tags.
            */}
            <View style={styles.stars}>
              {STARS.map(star => (
                <Pressable
                  key={star}
                  /* Not a button once it cannot be pressed - see
                     `ratingIsFinal`. A disabled button still announces itself
                     as something to press, which is the wrong thing to tell
                     somebody who is only being shown what they already left. */
                  accessibilityRole={ratingIsFinal ? 'image' : 'button'}
                  accessibilityState={{ selected: star <= shownStars }}
                  accessibilityLabel={
                    ratingIsFinal
                      ? `You rated this ${shownStars} out of 5`
                      : `Rate ${star} star${star === 1 ? '' : 's'}`
                  }
                  /* Five separate "you rated this 4 out of 5" is the sentence
                     read five times. Settled once it is a readout rather than
                     a control: the first star carries it, the rest step out of
                     the way. Both props, because the two platforms each honour
                     a different one. */
                  accessibilityElementsHidden={ratingIsFinal && star !== 1}
                  importantForAccessibility={
                    ratingIsFinal && star !== 1 ? 'no-hide-descendants' : 'auto'
                  }
                  disabled={ratingIsFinal}
                  onPress={() => {
                    setJustRated(star);
                    onQuickRate?.(booking, star);
                  }}
                  hitSlop={6}
                  style={({ pressed }) => (pressed ? styles.pressed : null)}
                >
                  <Icon
                    name={star <= shownStars ? 'starFilled' : 'star'}
                    size={30}
                    color={
                      star <= shownStars ? colors.rating : colors.borderStrong
                    }
                  />
                </Pressable>
              ))}
            </View>

            {/* Held open at both ends so the panel does not jump under the
                finger that just tapped a star. */}
            <Text variant="labelSm" align="center" color={colors.inkBody}>
              {STAR_WORDS[shownStars] || ' '}
            </Text>

            {/* What was said, read back. One review per booking, so this is
                the whole of it rather than the latest of several. */}
            {review ? (
              <View style={styles.reviewBack}>
                {review.comment ? (
                  <Text variant="bodySm" align="center" color={colors.inkBody}>
                    “{review.comment}”
                  </Text>
                ) : null}

                {review.tags.length ? (
                  <View style={styles.reviewTags}>
                    {review.tags.map(tag => (
                      <View key={tag} style={styles.reviewTag}>
                        <Text variant="caption" color={colors.inkBody}>
                          {tag}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : null}

                {visiblePhotos(review).length ? (
                  <View style={styles.reviewPhotos}>
                    <Icon name="image" size={13} color={colors.inkMuted} />
                    <Text variant="caption" color={colors.inkMuted}>
                      {visiblePhotos(review).length} photo
                      {visiblePhotos(review).length === 1 ? '' : 's'} attached
                    </Text>
                  </View>
                ) : null}
              </View>
            ) : null}

            {/* The long form. The stars above have already taken the rating,
                so this is only ever "I want to say more" - or, once something
                has been written, "I want to change what I said". */}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={
                review?.comment || review?.tags.length
                  ? 'Edit your review'
                  : 'Rate and review'
              }
              onPress={() => onRate?.(booking)}
              style={({ pressed }) => [
                styles.rateButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text variant="labelSm" color={colors.primary}>
                {review?.comment || review?.tags.length
                  ? 'Edit your review'
                  : 'Rate & Review'}
              </Text>
            </Pressable>
          </View>
        ) : booking.stage === 'service' ? (
          <View style={styles.section}>
            <Text variant="subtitle">Service in progress</Text>
            <Text variant="caption" color={colors.inkMuted}>
              Started at {clockTime(booking.startedAt)}
            </Text>

            <View style={styles.notice}>
              <Icon
                name="check"
                size={15}
                color={colors.primary}
                strokeWidth={2.4}
              />
              <Text variant="caption" color={colors.primary}>
                {booking.progress}
              </Text>
            </View>

            {/*
              The running clock.

              Three lines, in descending order of what the app can stand
              behind. The big one COUNTS DOWN to the end of the estimate. Under
              it sit the two fixed facts a countdown cannot carry - when the
              work actually started, and how long it was quoted for - so the
              ticking figure can always be checked against them.

              Once the estimate is spent the big line switches to counting up
              and the caption says so. See the file note on
              `data/progress.ts` for why zero is a switch rather than a stop.

              `progress` is null when the booking carries no readable start
              time, and the static estimate row below is shown instead. That
              is the honest fallback: no clock rather than a wrong one.
            */}
            {progress ? (
              <View style={styles.timer}>
                <View style={styles.timerHead}>
                  <Icon name="clock" size={15} color={colors.primary} />
                  <Text variant="labelSm" color={colors.inkBody}>
                    {progress.countdown ? 'Time left' : 'Time on the job'}
                  </Text>
                </View>

                {/*
                  The big number counts DOWN while the estimate has time in
                  it, and switches to counting UP once it does not.

                  The switch is the point. A countdown frozen at "0:00" over a
                  job still in progress is the app insisting the work is
                  finished while somebody is audibly still scrubbing; going
                  negative is worse. So when `countdown` is null - spent, or
                  never had an estimate to count against - the elapsed clock
                  takes the line back and the caption below explains why.
                */}
                <Text variant="displaySm">
                  {progress.countdown ?? progress.elapsed}
                </Text>

                {/*
                  The two fixed facts, under the moving one.

                  A countdown on its own is unanchored - "1:20:00" means
                  nothing without knowing what it is counting from or toward,
                  and those are exactly the two numbers a customer checks when
                  a job feels slow. `startedAt` is what the professional's
                  arrival was logged as; `duration` is what was quoted at
                  checkout. Neither ticks, which is why they can sit in one
                  quiet line beneath the figure that does.
                */}
                <Text variant="caption" color={colors.inkMuted}>
                  Started at {clockTime(booking.startedAt)} · Total{' '}
                  {booking.duration}
                </Text>

                {/*
                  Overrun, and ONLY overrun.

                  There is deliberately no "about 30 mins left" line while the
                  countdown is running: it restates the ticking figure above
                  it in worse units, and two numbers saying the same thing
                  invite the reader to check whether they agree. The clock
                  carries it.

                  Once the estimate is spent the clock has nothing left to
                  say, so this line takes over - stated, not alarmed about.
                  Still `inkMuted`, no red: the work is happening, and a
                  warning colour would invite a complaint about a job going
                  perfectly well.
                */}
                {progress.overrun ? (
                  <Text variant="caption" color={colors.inkMuted}>
                    {progress.elapsed} in · longer than estimated
                  </Text>
                ) : null}

                {/*
                  The bar DRAINS: full when the job starts, empty when the
                  estimate is spent. It reads as the time remaining rather
                  than the work completed - the app cannot see how much of a
                  bathroom is clean, and a bar that filled would look like it
                  could. What is genuinely being measured is the estimate
                  running out, and an emptying bar is that.

                  Measured against the TOP of the band, so empty lines up with
                  the moment the estimate is spent rather than the moment the
                  job is over. Purely decorative - the lines above are what a
                  screen reader announces, and a bar with no role adds nothing
                  for it to read twice.
                */}
                <View style={styles.timerTrack}>
                  <View
                    style={[
                      styles.timerFill,
                      {
                        width: `${Math.round((1 - progress.fraction) * 100)}%`,
                      },
                      progress.overrun ? styles.timerFillOverrun : null,
                    ]}
                  />
                </View>
              </View>
            ) : (
              <View style={styles.factRow}>
                <Icon name="clock" size={15} color={colors.inkMuted} />
                <Text
                  variant="caption"
                  color={colors.inkMuted}
                  style={styles.factFill}
                >
                  Estimated duration
                </Text>
                <Text variant="labelSm">{booking.duration}</Text>
              </View>
            )}

            {/*
              Keeping the professional on for another hour.

              Under the clock because that is where a customer looks when they
              realise the estimate will not be enough. Only on an hourly job -
              a fixed-price service is a job, not a span of time to add to -
              and it draws nothing when there is no room left under the cap.
            */}
            <ExtendTimeCard
              bookingId={booking.id}
              enabled={booking.hours != null}
              minutesLeft={minutesLeft}
              endsAt={runsUntil}
              onApplied={onRefresh}
            />

            {/*
              The END code - the second half of the OTP handshake.

              The start code proved the right person opened the door; this one
              proves the customer agreed the work is done. It is minted when
              the job starts and shown for the whole visit, so it is already
              on screen at the moment the professional asks for it - and the
              copy says plainly not to share it until the work is checked,
              because reading it out IS signing off.
            */}
            {booking.endOtp && !endCodeShown ? (
              <View style={styles.otpPanel}>
                <Text variant="labelSm">Finished?</Text>
                <Text variant="caption" color={colors.inkMuted}>
                  When the work is done, get your end code and read it out to
                  the professional. That is what closes the job.
                </Text>
                <Button
                  label="Get end code"
                  size="sm"
                  onPress={() => setCheckingBeforeCode(true)}
                />
              </View>
            ) : null}

            {booking.endOtp && endCodeShown ? (
              <View style={styles.otpPanel}>
                <Text variant="labelSm">End OTP</Text>
                <Text variant="caption" color={colors.inkMuted}>
                  Read this out to the professional to mark the job complete.
                </Text>

                <View style={styles.otpRow}>
                  {booking.endOtp.split('').map((digit, i) => (
                    <View key={i} style={styles.otpBox}>
                      <Text variant="titleLg">{digit}</Text>
                    </View>
                  ))}
                </View>

                {/*
                  The only way out of a lockout, and it has to live here.

                  Five wrong entries and the professional's app cannot complete
                  the job. Nothing on their handset can mint a new code - the
                  route is authenticated as the customer, deliberately, because
                  a code the professional could reissue for themselves would
                  not be a handshake at all.

                  Without this button that dead end is final: a mistyped digit
                  leaves somebody standing in a customer's home unable to close
                  the job, and the self-service resend that was built to
                  prevent exactly that would have had nothing to press.

                  Always offered, not only after a failure - this screen is
                  never told that the other side got it wrong.
                */}
                {onResendEndOtp ? (
                  <View style={styles.otpResend}>
                    <Pressable
                      onPress={resendCode}
                      disabled={resendingCode}
                      accessibilityRole="button"
                      accessibilityLabel="Get a new end code"
                      accessibilityState={{
                        disabled: resendingCode,
                        busy: resendingCode,
                      }}
                      hitSlop={spacing.sm}
                    >
                      <Text variant="labelSm" color={colors.primary}>
                        {resendingCode ? 'Sending…' : 'Get a new code'}
                      </Text>
                    </Pressable>

                    {resendNotice ? (
                      <Text variant="caption" color={colors.inkMuted}>
                        {resendNotice}
                      </Text>
                    ) : (
                      <Text variant="caption" color={colors.inkMuted}>
                        Use this if the professional says the code will not
                        work.
                      </Text>
                    )}
                  </View>
                ) : null}
              </View>
            ) : null}
          </View>
        ) : booking.otp ? (
          <View style={styles.section}>
            <Text variant="subtitle">Professional has arrived</Text>
            <Text variant="caption" color={colors.inkMuted}>
              {pro?.name} is at your location.
            </Text>

            {/*
              The code is read out, not typed in. It proves to the professional
              that they are at the right door, which is why it is shown rather
              than entered - and why the copy says to confirm the person first.
            */}
            <View style={styles.otpPanel}>
              <Text variant="labelSm">Start OTP</Text>
              <Text variant="caption" color={colors.inkMuted}>
                Share this OTP only after you have confirmed the professional
              </Text>

              <View style={styles.otpRow}>
                {booking.otp.split('').map((digit, i) => (
                  <View key={i} style={styles.otpBox}>
                    <Text variant="titleLg">{digit}</Text>
                  </View>
                ))}
              </View>

              <Text variant="caption" color={colors.inkMuted}>
                Share this code with {pro?.name.split(' ')[0]} to begin the
                service.
              </Text>
            </View>
          </View>
        ) : booking.eta ? (
          <View style={styles.section}>
            <Text variant="subtitle">Professional is on the way</Text>

            {/*
              ----------------------------------------------------------------
              NO TRACKING. THE ETA IS THE WHOLE PANEL.
              ----------------------------------------------------------------
              This deliberately shows a time and nothing else - no map, no
              distance, no live position, no "2.4 km away".

              Two reasons, and they point the same way:

                1. There is nothing to track. `react-native-maps` is installed
                   and `LocationScreen` uses it, but no professional's position
                   is being fed to this app. A map here could only plot a route
                   somebody invented, and an invented route is worse than no
                   route - it looks like tracking, so it gets believed, and the
                   van that never appears at the corner is the app lying.

                2. It is somebody's live location. Continuously showing where a
                   worker is, to a customer, for the length of a shift, is a
                   surveillance feature wearing a convenience costume. "Are
                   they close" is answered by a number. A dot moving down a
                   street answers a different question that nobody needs
                   answered.

              The distance line and the live-location notice that used to sit
              here are commented out below rather than deleted, so that turning
              this back on is a decision someone makes on purpose rather than a
              panel they have to rebuild from memory.

              `booking.distance` is still on the type and still in the seed
              data - nothing reads it now. See `src/data/bookings.ts`.
            */}
            <View style={styles.etaRow}>
              <View style={styles.etaText}>
                <Text variant="caption" color={colors.inkMuted}>
                  Arriving in
                </Text>
                <Text variant="display" color={colors.primary}>
                  {booking.eta}
                </Text>

                {/* Was: how far away they are, from `booking.distance`.
                <Text variant="caption" color={colors.inkMuted}>
                  {booking.distance}
                </Text>
                */}
              </View>
            </View>

            {/* Was: the standing note that live location had not been wired
                up. It only made sense as an apology for a missing map, and
                there is no missing map now - this is the panel.
            <View style={styles.notice}>
              <Icon name="pin" size={15} color={colors.primary} />
              <Text variant="caption" color={colors.primary}>
                Live location is not connected yet
              </Text>
            </View>
            */}

            {/* What can be said honestly instead: the ETA is an estimate, and
                the way to ask about it is to ask the person. */}
            <View style={styles.notice}>
              <Icon name="clock" size={15} color={colors.primary} />
              <Text variant="caption" color={colors.primary}>
                Estimated arrival - message your professional for an update
              </Text>
            </View>
          </View>
        ) : awaitingPayment ? (
          /*
           * The one panel that replaces "Finding a professional" entirely.
           *
           * Nothing is being found and saying so would be false. What is
           * true: the slot is held, the money has not been taken, and the
           * booking goes away on its own if it stays that way. The button is
           * the point - without it the only route back to paying is to book
           * the whole thing again.
           */
          <View style={styles.section}>
            <View style={styles.pendingCard}>
              <View style={styles.pendingMark}>
                <Icon name="clock" size={18} color={colors.inkBody} />
              </View>
              <View style={styles.factFill}>
                <Text variant="subtitle">Not confirmed yet</Text>
                <Text variant="caption" color={colors.inkMuted}>
                  Your slot is held but the payment has not gone through.
                  Nothing has been charged, and this booking is released if it
                  stays unpaid.
                </Text>
              </View>
            </View>

            {onPay ? (
              <Button
                label={`Pay ₹${booking.price} now`}
                style={styles.pendingAction}
                onPress={() => onPay(booking)}
              />
            ) : null}
          </View>
        ) : (
          <View style={styles.section}>
            <Text variant="subtitle">
              {pro ? 'Your professional is assigned' : 'Finding a professional'}
            </Text>
            <Text variant="caption" color={colors.inkMuted}>
              {pro
                ? `${pro.name} will handle your booking.`
                : 'We will assign someone shortly.'}
            </Text>
          </View>
        )}

        {/* ------------------------------------------------ the professional */}
        {pro ? (
          <View style={styles.section}>
            <ProCard
              pro={pro}
              onMessage={() => onMessage?.(booking)}
              onCall={() =>
                onCall?.(booking, callable.data?.counterpart?.displayName)
              }
              canCall={!!onCall && (callGated || !!callable.data?.canCall)}
              callReason={eligibilityCopy(callable.data?.reason ?? null)}
              /* The other two states of the same request. Without them the
                 button was dark and captionless while it was in flight, and
                 dark and captionless for ever if it failed. Both are false
                 while gated: nothing has been asked, so nothing is pending
                 and nothing has failed. */
              callLoading={!callGated && callable.loading}
              callFailed={!callGated && !!callable.error}
              onRetryCall={callable.reload}
            />
          </View>
        ) : null}

        {/*
          Cancelled, and by whom.

          An ops cancellation is not the customer's doing and must not read
          like it: it apologises, and it offers the one thing that actually
          helps - booking the same thing again. A customer cancellation states
          the fact and leaves it there.
        */}
        {cancelled ? (
          <View style={styles.section}>
            <View style={styles.cancelBanner}>
              <View style={styles.cancelMark}>
                <Icon name="alert" size={18} color={colors.inkBody} />
              </View>
              <View style={styles.factFill}>
                <Text variant="subtitle">Booking cancelled</Text>
                <Text variant="caption" color={colors.inkMuted}>
                  {booking.progress ??
                    'This booking was cancelled and will not go ahead.'}
                </Text>
              </View>
            </View>

            {onRebook ? (
              <Button
                label="Book this again"
                variant="secondary"
                style={styles.cancelAction}
                onPress={() => onRebook(booking)}
              />
            ) : null}
          </View>
        ) : null}

        {/*
          The money coming back.

          `refund` (both halves of the journey - raised, then settled, with
          dates for each) only ever exists for the bundled demo bookings:
          `refundFor` is a seeded lookup, and there is no server field it
          could read instead - the platform tells this app an AMOUNT, never a
          settlement status or a date. A real cancelled booking falls to the
          simpler summary below instead of showing nothing, but it does not
          get to claim a two-step timeline this app cannot actually confirm.
        */}
        {refund ? (
          <View style={styles.section}>
            <RefundTimeline refund={refund} />
          </View>
        ) : cancelled &&
          (booking.refundedAmount !== undefined ||
            booking.coinsRefunded !== undefined) ? (
          <View style={styles.section}>
            <View style={styles.refundCard}>
              {/*
                Money and coins are two refunds, not one.

                They settle down different pipes and on different clocks: the
                card refund goes through the gateway and the bank posts it in
                5–7 days, while the coins are back on the ledger the instant
                the cancellation lands. Adding them into a single rupee figure
                is what makes somebody wait a week for coins they can already
                spend - so each gets its own line, with its own timing note.

                A booking paid entirely in coins has no `refundedAmount` at
                all, which is why the head is conditional rather than assumed.
              */}
              {booking.refundedAmount !== undefined ? (
                <View style={styles.refundHead}>
                  <View style={styles.refundMark}>
                    <Icon name="refund" size={18} color={colors.primary} />
                  </View>
                  <Text variant="subtitle">
                    {rupees(booking.refundedAmount)} refunded
                  </Text>
                </View>
              ) : null}

              {booking.coinsRefunded ? (
                <View style={styles.refundHead}>
                  <View style={styles.refundMark}>
                    <Icon name="wallet" size={18} color={colors.primary} />
                  </View>
                  <Text variant="subtitle">
                    {booking.coinsRefunded} coins returned
                  </Text>
                </View>
              ) : null}

              {booking.cancellationFeeAmount ? (
                <View style={styles.refundSplit}>
                  <Text variant="caption" color={colors.inkMuted}>
                    Cancellation fee kept
                  </Text>
                  <Text variant="caption" color={colors.inkBody}>
                    {rupees(booking.cancellationFeeAmount)}
                  </Text>
                </View>
              ) : null}

              {/*
                Coins first, because it is the one that has already happened.
                Leading with the bank's 5–7 days over a refund that is already
                back reads as though nothing has.
              */}
              {booking.coinsRefunded ? (
                <Text variant="caption" color={colors.inkMuted}>
                  Coins are already back in your wallet and can be used on your
                  next booking.
                </Text>
              ) : null}

              {booking.refundedAmount !== undefined ? (
                <Text variant="caption" color={colors.inkMuted}>
                  Back to the original payment method. Banks can take 5–7
                  working days to post it.
                </Text>
              ) : null}
            </View>
          </View>
        ) : null}

        {/* One tap from the visit it is about, and only while one is under
            way - see `live`. */}
        {live && onSos ? (
          <View style={styles.section}>
            <SosButton onPress={() => onSos(booking)} />
          </View>
        ) : null}

        {/* ---------------------------------------------------- the stepper */}
        <View style={styles.section}>
          <View style={styles.stepper}>
            {bookingStages.map((step, i) => {
              const passed = i <= current;
              return (
                <React.Fragment key={step.key}>
                  {i > 0 ? (
                    <View
                      style={[styles.track, passed ? styles.trackOn : null]}
                    />
                  ) : null}

                  <View style={styles.step}>
                    <View style={[styles.dot, passed ? styles.dotOn : null]}>
                      {passed ? (
                        <Icon
                          name="check"
                          size={11}
                          color={colors.onPrimary}
                          strokeWidth={3}
                        />
                      ) : null}
                    </View>
                    <Text
                      variant="caption"
                      align="center"
                      numberOfLines={1}
                      color={passed ? colors.ink : colors.inkSubtle}
                      style={styles.stepLabel}
                    >
                      {step.label}
                    </Text>
                  </View>
                </React.Fragment>
              );
            })}
          </View>
        </View>

        {/* ----------------------------------------------------- the sections */}
        <Row icon="pin" title="Service Address" detail={booking.address} />

        <Disclosure
          icon="creditCard"
          title="Payment Details"
          open={paymentOpen}
          onToggle={() => setPaymentOpen(open => !open)}
          summary={
            booking.payment.paid > 0
              ? `Paid ${rupees(booking.payment.paid)} (${
                  booking.payment.method
                })`
              : booking.payment.method
          }
        >
          <PriceLine label="Booking total" value={rupees(booking.price)} />
          <PriceLine
            label={`Paid (${booking.payment.method})`}
            value={rupees(booking.payment.paid)}
          />
          <PriceLine
            label="Due at completion"
            value={rupees(Math.max(0, booking.price - booking.payment.paid))}
          />
        </Disclosure>

        {/*
          A link once there is a document, a plain line until then.

          Nothing is more annoying than a chevron that opens an empty page, so
          the row is only pressable when `hasInvoice` says a tax invoice
          actually exists - issued on completion, never on payment.
        */}
        {hasInvoice(booking) && onOpenInvoice ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="View tax invoice"
            onPress={() => onOpenInvoice(booking)}
            style={({ pressed }) => [
              styles.section,
              pressed ? styles.pressed : null,
            ]}
          >
            <View style={styles.rowHead}>
              <Icon name="document" size={17} color={colors.primary} />
              <View style={styles.factFill}>
                <Text variant="subtitle">Invoice</Text>
                <Text variant="caption" color={colors.inkMuted}>
                  Tax invoice for {booking.reference}
                </Text>
              </View>
              <Icon name="chevronRight" size={16} color={colors.borderStrong} />
            </View>
          </Pressable>
        ) : (
          <View style={styles.section}>
            <View style={styles.rowHead}>
              <Icon name="document" size={17} color={colors.borderStrong} />
              <View style={styles.factFill}>
                <Text variant="subtitle" color={colors.inkMuted}>
                  Invoice
                </Text>
                <Text variant="caption" color={colors.inkMuted}>
                  Issued once the work is marked complete
                </Text>
              </View>
            </View>
          </View>
        )}

        <Disclosure
          icon="clock"
          title="Cancellation & Refund"
          open={policyOpen}
          onToggle={() => setPolicyOpen(open => !open)}
          summary="View cancellation & refund policy"
        >
          {/*
            From the policy object, not typed here.

            This line used to read "Cancel or reschedule at no cost up to 6
            hours before the visit" - one figure covering two different rules.
            Rescheduling is free at any distance and needs two hours' notice;
            six is the cancellation window. A customer who read this and then
            met "2 hours" in the slot picker saw the app contradict itself.
          */}
          <Text variant="bodySm" color={colors.inkBody}>
            {cancellationPolicy().detail}
          </Text>

          {/*
            Moving it comes before cancelling, and is the calmer control.

            Someone who cannot make their slot usually wants a different slot,
            not a refund - offering the refund first pushes them toward
            cancelling a booking they would have kept. Same eligibility as
            cancelling, less `onway` and `service`: once a professional has
            set off there is nothing left to move, and `canReschedule` is what
            says so.
          */}
          {canReschedule(booking) && onReschedule ? (
            <Button
              label="Reschedule this booking"
              variant="secondary"
              size="md"
              style={styles.cancelAction}
              onPress={() => onReschedule(booking)}
            />
          ) : null}

          {/*
            The action lives with the policy that governs it, and it is the
            quiet variant on purpose: cancelling is available, not encouraged.
            What it costs is decided by the sheet, from the stage - see
            `data/cancellation.ts`.
          */}
          {canCancel(booking) && onCancel ? (
            <Button
              label="Cancel this booking"
              variant="danger"
              size="md"
              style={styles.cancelAction}
              onPress={() => onCancel(booking)}
            />
          ) : null}
        </Disclosure>

        {/*
          Disputing is offered only on a finished job, because a job in flight
          is cancelled instead. Two different evidence bars, two different
          consequences - never the same control.
        */}
        {done && onDispute ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Something wrong with this job? Raise a dispute"
            onPress={() => onDispute(booking)}
            style={({ pressed }) => [
              styles.section,
              pressed ? styles.pressed : null,
            ]}
          >
            <View style={styles.rowHead}>
              <Icon name="alert" size={17} color={colors.primary} />
              <View style={styles.factFill}>
                <Text variant="subtitle">Something wrong with this job?</Text>
                <Text variant="caption" color={colors.inkMuted}>
                  Raise a dispute and we will review the evidence
                </Text>
              </View>
              <Icon name="chevronRight" size={16} color={colors.borderStrong} />
            </View>
          </Pressable>
        ) : null}

        {/*
          --------------------------------------------------- support, out loud

          This was a `Disclosure` - "Need Help?", "Support, FAQs and more" -
          which opened to a sentence telling the customer to contact support
          and gave them no way to do it. Two taps to reach a dead end, on the
          screen somebody opens precisely because something has gone wrong.

          A BUTTON, not another card. Everything else on this page is a panel
          of facts you read, and a help row drawn the same way was read the
          same way - as one more thing to scroll past. This is the only control
          in the column and it looks like one.

          WhatsApp rather than a phone number because it is what the customer
          already has open, it survives a bad signal, and it leaves the desk a
          written record with the booking reference in it.
        */}
        <View style={styles.support}>
          <Button
            label="Chat on WhatsApp"
            variant="whatsapp"
            leadingIcon="whatsapp"
            accessibilityLabel={`Chat with support on WhatsApp about booking ${booking.reference}`}
            onPress={messageSupport}
          />
          {/* Under the button, not on it: an expectation, not an instruction.
              A second line inside the control would make the label a
              paragraph. */}
          <Text
            variant="caption"
            color={colors.inkMuted}
            style={styles.supportNote}
          >
            Need help with this booking? We usually reply in minutes.
          </Text>
        </View>

        <View style={styles.footNote}>
          <Icon name="shieldCheck" size={16} color={colors.primary} />
          <Text
            variant="caption"
            color={colors.inkBody}
            style={styles.factFill}
          >
            Critical updates will also be sent via WhatsApp / SMS
          </Text>
        </View>
      </ScrollView>

      {/*
        The one question worth asking in the whole visit.

        Neutral, not danger: nothing is being destroyed and a red sheet over
        a job that went perfectly well reads as an accusation. The confirm
        button says what happens next rather than "Yes", and the way out is
        "Not yet" - the customer who presses it is going to look round the
        room, which is exactly the point.
      */}
      <ConfirmSheet
        visible={checkingBeforeCode}
        tone="neutral"
        icon="shieldCheck"
        title="Before you share the code"
        body="Reading the code out marks the job complete and pays the professional. Have a quick look round first."
        confirmLabel="All good, show my code"
        cancelLabel="Not yet"
        onConfirm={() => {
          setCheckingBeforeCode(false);
          setEndCodeShown(true);
        }}
        onClose={() => setCheckingBeforeCode(false)}
      >
        <View style={styles.checkList}>
          {[
            'The work is finished the way you wanted',
            'Your belongings are where they should be',
            'Nothing has been damaged',
          ].map(line => (
            <View key={line} style={styles.checkRow}>
              <Icon name="check" size={15} color={colors.primary} />
              <Text
                variant="bodySm"
                color={colors.inkBody}
                style={styles.factFill}
              >
                {line}
              </Text>
            </View>
          ))}
        </View>
      </ConfirmSheet>
    </View>
  );
}

// ---------------------------------------------------------------- small parts

/**
 * The professional, with the two ways to reach them.
 *
 * Their real photograph when the platform sends one - a short-lived signed
 * URL on `pro.photoUrl`, read fresh off the booking each load - and their
 * initials when it does not. The initials are a design, not a placeholder:
 * most Pros have no photo yet, and a grey silhouette on a card that names a
 * real person reads as something missing. What is never shown is a stock
 * face; the old note here refusing photographs was about exactly that, from
 * before the platform could supply genuine ones.
 */
function ProCard({
  pro,
  onMessage,
  onCall,
  canCall,
  callReason,
  callLoading = false,
  callFailed = false,
  onRetryCall,
}: {
  pro: Professional;
  onMessage: () => void;
  onCall: () => void;
  /** False until the platform says this professional can be reached. */
  canCall: boolean;
  /** Why not, when not. Null when there is nothing useful to say. */
  callReason: string | null;
  /**
   * The eligibility request is still in flight.
   *
   * Drawn inert with no caption, which is what `CallButton` does and for the
   * same reason: a control that has not been refused yet must not say it has.
   */
  callLoading?: boolean;
  /** The eligibility request failed. Different from "refused" - say so. */
  callFailed?: boolean;
  /** Ask again. Only offered on a failure; a refusal is an answer. */
  onRetryCall?: () => void;
}) {
  const initials = pro.name
    .split(' ')
    .map(part => part[0])
    .slice(0, 2)
    .join('');

  /*
   * Three reasons the button can be dark, and they are not the same thing.
   *
   *   loading   nobody has answered yet   ->  inert, and say nothing
   *   failed    nobody could be asked     ->  inert, say so, offer a retry
   *   refused   the platform said no      ->  inert, say which no
   *
   * `CallButton` already draws this distinction; this card is the copy that
   * did not, and the gap showed as a control that went dark on a dropped
   * request and stayed dark, captionless and unexplained, until the screen
   * was closed. A failure is the one state with something to press.
   */
  const inert = callLoading || callFailed || !canCall;
  const caption = callLoading
    ? null
    : callFailed
    ? 'Could not check whether calling is available.'
    : canCall
    ? null
    : callReason;

  return (
    <>
      <View style={styles.proRow}>
        {pro.photoUrl ? (
          <Image
            source={{ uri: pro.photoUrl }}
            style={styles.avatarPhoto}
            /* Portraits arrive at whatever aspect the Pro's camera took, so
               the circle crops rather than letterboxes. */
            resizeMode="cover"
            accessible
            accessibilityRole="image"
            accessibilityLabel={`${pro.name}'s photo`}
          />
        ) : (
          <View style={styles.avatar}>
            <Text variant="subtitle" color={colors.onPrimary}>
              {initials}
            </Text>
          </View>
        )}

        <View style={styles.proText}>
          <View style={styles.proName}>
            <Text variant="subtitle">{pro.name}</Text>
            {pro.verified ? (
              <Icon name="shieldCheck" size={14} color={colors.primary} />
            ) : null}
          </View>

          <View style={styles.proMeta}>
            <Icon name="starFilled" size={12} color={colors.rating} />
            <Text variant="caption" color={colors.inkMuted}>
              {pro.rating.toFixed(1)} ({pro.jobs} jobs)
            </Text>
          </View>

          <Text variant="caption" color={colors.inkMuted}>
            {pro.title}
          </Text>
        </View>
      </View>

      <View style={styles.proActions}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Message ${pro.name}`}
          onPress={onMessage}
          style={({ pressed }) => [
            styles.ghost,
            pressed ? styles.pressed : null,
          ]}
        >
          <Icon name="chat" size={16} color={colors.primary} />
          <Text variant="labelSm" color={colors.primary}>
            Message
          </Text>
        </Pressable>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Call ${pro.name}`}
          accessibilityState={{ disabled: inert, busy: callLoading }}
          accessibilityHint={
            inert ? caption ?? undefined : 'Places a call over the internet.'
          }
          disabled={inert}
          onPress={onCall}
          style={({ pressed }) => [
            styles.filled,
            pressed ? styles.pressed : null,
            inert ? styles.inert : null,
          ]}
        >
          <Icon name="phoneFilled" size={16} color={colors.onPrimary} />
          <Text variant="labelSm" color={colors.onPrimary}>
            Call in app
          </Text>
        </Pressable>
      </View>

      {/* Why the call button is dark, on the control rather than behind a
          press. A working button needs no caption, and neither does one that
          is merely still asking - so this appears only once there is an actual
          answer, refusal or failure. */}
      {caption ? (
        <Text
          variant="caption"
          color={colors.inkMuted}
          style={styles.callReason}
        >
          {caption}
        </Text>
      ) : null}

      {/*
        A retry, and only for the state a retry can fix.

        A refusal is an answer - pressing again gets the same one - so nothing
        is offered there. A request that never landed is the opposite: it is
        the one case where the customer knows something the app does not, which
        is that they are still here and would like it to try again.
      */}
      {callFailed && onRetryCall ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Try again"
          onPress={onRetryCall}
          hitSlop={8}
          style={({ pressed }) => [
            styles.callRetry,
            pressed ? styles.pressed : null,
          ]}
        >
          <Text variant="labelSm" color={colors.primary}>
            Try again
          </Text>
        </Pressable>
      ) : null}
    </>
  );
}

/** A section that opens. Every one on this page shares the shape. */
function Disclosure({
  icon,
  title,
  detail,
  summary,
  open,
  onToggle,
  children,
}: {
  icon: IconName;
  title: string;
  detail?: string;
  summary?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <View style={styles.section}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        accessibilityLabel={title}
        onPress={onToggle}
        style={styles.rowHead}
      >
        <Icon name={icon} size={17} color={colors.primary} />
        <View style={styles.factFill}>
          <Text variant="subtitle">{title}</Text>
          {summary ? (
            <Text variant="caption" color={colors.inkMuted} numberOfLines={1}>
              {summary}
            </Text>
          ) : null}
        </View>
        {detail ? (
          <Text variant="caption" color={colors.inkMuted}>
            {detail}
          </Text>
        ) : null}
        <Icon
          name={open ? 'chevronDown' : 'chevronRight'}
          size={16}
          color={colors.borderStrong}
        />
      </Pressable>

      {open ? <View style={styles.rowBody}>{children}</View> : null}
    </View>
  );
}

/**
 * A section that states a fact and goes nowhere.
 *
 * No chevron: this is a plain `View`, not a `Pressable` - there is nothing to
 * open here, the detail line already shows the whole thing. A chevron that
 * leads nowhere reads as a broken link, not as "there's more".
 */
function Row({
  icon,
  title,
  detail,
}: {
  icon: IconName;
  title: string;
  detail: string;
}) {
  return (
    <View style={styles.section}>
      <View style={styles.rowHead}>
        <Icon name={icon} size={17} color={colors.primary} />
        <View style={styles.factFill}>
          <Text variant="subtitle">{title}</Text>
          <Text variant="caption" color={colors.inkMuted}>
            {detail}
          </Text>
        </View>
      </View>
    </View>
  );
}

function PriceLine({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.priceLine}>
      <Text variant="bodySm" color={colors.inkBody}>
        {label}
      </Text>
      <Text variant="bodySm" color={colors.inkBody}>
        {value}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  /* Cancelled, and the way back out of it. */
  cancelBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  cancelMark: {
    width: 42,
    height: 42,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceMuted,
  },
  cancelAction: {
    marginTop: spacing.md,
  },
  /* Same shape as the cancel banner: an icon, a fact, and one way forward. */
  pendingCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  pendingMark: {
    width: 42,
    height: 42,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceMuted,
  },
  pendingAction: {
    marginTop: spacing.md,
  },
  /* The simple, real-data refund summary - see the note above `RefundTimeline`
     on why this exists alongside it rather than instead of it. */
  refundCard: {
    gap: spacing.sm,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  refundHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  refundMark: {
    width: 42,
    height: 42,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  refundSplit: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: spacing.sm,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  /* What was said in the review, read back under the stars. */
  reviewBack: {
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  reviewTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  reviewTag: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceAccent,
  },
  reviewPhotos: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
    backgroundColor: colors.surface,
    borderBottomWidth: hairline.borderWidth,
    borderBottomColor: hairline.borderColor,
  },
  headerTitle: {
    flex: 1,
  },
  /* Wide enough for the larger glyph without the two touching each other. */
  headerIcon: {
    width: 30,
    alignItems: 'center',
  },

  /* The inset lives here, so every card sits on the same margin and nothing
     has to repeat it. */
  page: {
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
  },
  /*
   * Cards, not bands.
   *
   * This page is a set of separate facts - who is coming, what was paid, the
   * terms - read one at a time and mostly folded away. Discrete cards say that;
   * edge-to-edge bands say one continuous document, which is what the cart is
   * and this is not.
   */
  section: {
    gap: spacing.xs,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },

  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  badge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.sm,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  badgeDone: {
    borderColor: colors.borderStrong,
  },
  title: {
    marginTop: spacing.sm,
  },
  factRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },

  /* The running clock. Its own panel, because it is the one thing on this
     screen that changes while you watch it. */
  timer: {
    gap: 2,
    padding: spacing.lg,
    marginTop: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  timerHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  timerTrack: {
    height: 4,
    marginTop: spacing.sm,
    borderRadius: 2,
    overflow: 'hidden',
    backgroundColor: colors.border,
  },
  timerFill: {
    height: '100%',
    borderRadius: 2,
    backgroundColor: colors.primary,
  },
  /* Spent estimate reads quieter, not louder - see the overrun note above. */
  timerFillOverrun: {
    backgroundColor: colors.borderStrong,
  },
  factFill: {
    flex: 1,
  },

  serviceLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },

  // --------------------------------------------------------- the stage panels
  notice: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.sm,
    padding: spacing.sm,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceAccent,
  },
  etaRow: {
    marginTop: spacing.sm,
  },
  etaText: {
    gap: 2,
  },

  otpPanel: {
    gap: spacing.xs,
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceAccent,
  },
  /* Set apart from the digits above it, so a tap meant for the code cannot
     land on the control that replaces it. */
  otpResend: {
    gap: spacing.xs,
    marginTop: spacing.xs,
    alignItems: 'center',
  },
  otpRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginVertical: spacing.sm,
  },
  /* The three things to look at before the code is read out, inside the
     confirmation sheet. Left-aligned, because a list is read down. */
  checkList: {
    gap: spacing.xs,
    width: '100%',
    marginTop: spacing.xs,
  },
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  /*
   * Flexible, not fixed at 48pt.
   *
   * Six fixed 48pt boxes plus five 8pt gaps is 328pt of row, inside a card
   * that pays the page gutter and its own padding twice each - 376pt on a
   * 360pt phone, so the last digit of the code ran off the screen. Flexed,
   * the six divide whatever the card actually has, and `maxWidth` keeps a
   * four-digit code from inflating into slabs.
   */
  otpBox: {
    flex: 1,
    maxWidth: 48,
    height: 54,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.sm,
    backgroundColor: colors.surface,
    ...hairline,
  },

  doneTitle: {
    marginBottom: spacing.md,
  },
  doneMark: {
    alignSelf: 'center',
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
    marginBottom: spacing.sm,
  },
  rateTitle: {
    marginTop: spacing.lg,
  },
  stars: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: spacing.md,
    marginVertical: spacing.md,
  },
  rateButton: {
    alignItems: 'center',
    paddingVertical: spacing.md,
    borderRadius: radii.sm,
    borderWidth: 1,
    borderColor: colors.primary,
  },

  // ------------------------------------------------------- the professional
  proRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  /* Same footprint as the initials disc, so the row does not shift when a
     photo arrives on a refresh. The tinted ground shows while it loads. */
  avatarPhoto: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: colors.surfaceAccent,
  },
  proText: {
    flex: 1,
    gap: 2,
  },
  proName: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  proMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  proActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  ghost: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    borderRadius: radii.sm,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  filled: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    borderRadius: radii.sm,
    backgroundColor: colors.primary,
  },

  // -------------------------------------------------------------- the stepper
  stepper: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  step: {
    width: 56,
    alignItems: 'center',
    gap: spacing.xs,
  },
  /* Grows to fill between two dots, and sits level with their centres. */
  track: {
    flex: 1,
    height: 2,
    marginTop: 9,
    backgroundColor: colors.border,
  },
  trackOn: {
    backgroundColor: colors.primary,
  },
  dot: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  dotOn: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
  },
  stepLabel: {
    fontSize: 10,
  },

  // ------------------------------------------------------------- the sections
  /* No card around it. The button carries its own weight, and a panel behind
     it would put it back among the things that are read rather than pressed. */
  support: {
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  supportNote: {
    textAlign: 'center',
  },
  rowHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  rowBody: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  priceLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.xs,
  },

  /* No horizontal margin of its own - the page's padding already sets it, and
     doubling up would inset this one strip further than every card above it. */
  footNote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceAccent,
  },
  pressed: {
    opacity: 0.6,
  },
  inert: {
    opacity: 0.45,
  },
  callReason: {
    marginTop: spacing.sm,
  },
  callRetry: {
    marginTop: spacing.xs,
    alignSelf: 'center',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
});

export default BookingDetailScreen;
